import { FormEvent, useEffect, useState } from 'react';
import { userService } from '../services/userService';
import Button from './Button';
import Loader from './Loader';
import { API_BASE } from '../services/httpClient';

const SecuritySettings = () => {
  const [enableForm, setEnableForm] = useState({ secret: '', code: '', password: '' });
  const [disableForm, setDisableForm] = useState({ code: '', password: '' });
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [securityStatus, setSecurityStatus] = useState<{ totp_enabled: boolean; totp_enabled_at?: string } | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    new_password_repeat: ''
  });
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [totpLoading, setTotpLoading] = useState(false);

  const refreshSecurityStatus = () => {
    setLoadingStatus(true);
    userService
      .getSecurityStatus()
      .then((response) => {
        setSecurityStatus(response.data);
        setStatusError(null);
      })
      .catch((err) => setStatusError(err instanceof Error ? err.message : 'Unable to load security status'))
      .finally(() => setLoadingStatus(false));
  };

  useEffect(() => {
    refreshSecurityStatus();
  }, []);

  const handleEnableTOTP = async (event: FormEvent) => {
    event.preventDefault();
    setStatus(null);
    setError(null);
    setTotpLoading(true);
    try {
      const response = await userService.enableTotp(enableForm);
      setBackupCodes(response.backup_codes);
      setStatus(response.message);
      setEnableForm({ secret: '', code: '', password: '' });
      refreshSecurityStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enable TOTP');
    } finally {
      setTotpLoading(false);
    }
  };

  const handleDisableTOTP = async (event: FormEvent) => {
    event.preventDefault();
    setStatus(null);
    setError(null);
    setTotpLoading(true);
    try {
      const response = await userService.disableTotp(disableForm);
      setStatus(response.message);
      setDisableForm({ code: '', password: '' });
      refreshSecurityStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable TOTP');
    } finally {
      setTotpLoading(false);
    }
  };

  const openPasskeyManager = () => {
    window.location.assign(`${API_BASE}/v2/passkey/manage.php`);
  };

  const handlePasswordChange = async (event: FormEvent) => {
    event.preventDefault();
    setPasswordLoading(true);
    setPasswordError(null);
    setPasswordMessage(null);
    try {
      const response = await userService.changePassword(passwordForm);
      setPasswordMessage(response.data.message || 'Password updated successfully.');
      setPasswordForm({ current_password: '', new_password: '', new_password_repeat: '' });
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Unable to update password');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="section" style={{ gap: '1rem' }}>
      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div>
          <h3>Two-factor authentication</h3>
          <p style={{ color: 'var(--gray)' }}>Protect your account with a time-based one time password.</p>
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <div className={`status-chip ${securityStatus?.totp_enabled ? 'success' : 'danger'}`}>
            <span>TOTP</span>
            <strong>{securityStatus?.totp_enabled ? 'Enabled' : 'Not enabled'}</strong>
          </div>
          {loadingStatus && <Loader size={20} color="var(--gray)" />}
          {statusError && <span style={{ color: 'var(--danger)' }}>{statusError}</span>}
        </div>
        {!securityStatus?.totp_enabled ? (
          <form onSubmit={handleEnableTOTP} style={{ display: 'grid', gap: '0.8rem' }}>
            <strong>Enable TOTP</strong>
            <div className="input-group">
              <label htmlFor="totp-secret">Secret</label>
              <input
                id="totp-secret"
                placeholder="e.g. JBSWY3DPEHPK3PXP"
                value={enableForm.secret}
                onChange={(event) => setEnableForm((prev) => ({ ...prev, secret: event.target.value }))}
                required
              />
            </div>
            <div className="input-group">
              <label htmlFor="totp-code">Verification code</label>
              <input
                id="totp-code"
                placeholder="e.g. 123456"
                value={enableForm.code}
                onChange={(event) => setEnableForm((prev) => ({ ...prev, code: event.target.value }))}
                required
              />
            </div>
            <div className="input-group">
              <label htmlFor="totp-password">Password confirmation</label>
              <input
                id="totp-password"
                type="password"
                placeholder="e.g. SuperSecure123!"
                value={enableForm.password}
                onChange={(event) => setEnableForm((prev) => ({ ...prev, password: event.target.value }))}
                required
              />
            </div>
            <Button type="submit" loading={totpLoading}>
              Enable TOTP
            </Button>
          </form>
        ) : (
          <form onSubmit={handleDisableTOTP} style={{ display: 'grid', gap: '0.8rem' }}>
            <strong>Disable TOTP</strong>
            <div className="input-group">
              <label htmlFor="disable-code">Current TOTP code</label>
              <input
                id="disable-code"
                placeholder="e.g. 789012"
                value={disableForm.code}
                onChange={(event) => setDisableForm((prev) => ({ ...prev, code: event.target.value }))}
                required
              />
            </div>
            <div className="input-group">
              <label htmlFor="disable-password">Password</label>
              <input
                id="disable-password"
                type="password"
                placeholder="e.g. SuperSecure123!"
                value={disableForm.password}
                onChange={(event) => setDisableForm((prev) => ({ ...prev, password: event.target.value }))}
                required
              />
            </div>
            <Button variant="danger" type="submit" loading={totpLoading}>
              Disable TOTP
            </Button>
          </form>
        )}
        {backupCodes.length > 0 && (
          <div>
            <strong>Backup codes</strong>
            <ul>
              {backupCodes.map((code) => (
                <li key={code}>{code}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div>
          <h3>Change password</h3>
          <p style={{ color: 'var(--gray)' }}>Update your sign-in password. Make sure it is unique and at least 8 characters.</p>
        </div>
        <form onSubmit={handlePasswordChange} style={{ display: 'grid', gap: '0.8rem' }}>
          <div className="input-group">
            <label htmlFor="current_password">Current password</label>
            <input
              id="current_password"
              type="password"
              name="current_password"
              placeholder="e.g. OldPassword789!"
              value={passwordForm.current_password}
              onChange={(event) => setPasswordForm((prev) => ({ ...prev, current_password: event.target.value }))}
              required
            />
          </div>
          <div className="input-group">
            <label htmlFor="new_password">New password</label>
            <input
              id="new_password"
              type="password"
              name="new_password"
              placeholder="e.g. BrandNewPass456!"
              value={passwordForm.new_password}
              onChange={(event) => setPasswordForm((prev) => ({ ...prev, new_password: event.target.value }))}
              required
            />
          </div>
          <div className="input-group">
            <label htmlFor="new_password_repeat">Repeat new password</label>
            <input
              id="new_password_repeat"
              type="password"
              name="new_password_repeat"
              placeholder="e.g. BrandNewPass456!"
              value={passwordForm.new_password_repeat}
              onChange={(event) => setPasswordForm((prev) => ({ ...prev, new_password_repeat: event.target.value }))}
              required
            />
          </div>
          {passwordMessage && <p style={{ color: 'var(--success)' }}>{passwordMessage}</p>}
          {passwordError && <p style={{ color: 'var(--danger)' }}>{passwordError}</p>}
          <Button type="submit" loading={passwordLoading}>
            Update password
          </Button>
        </form>
      </div>
      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div>
          <h3>Passkeys</h3>
          <p style={{ color: 'var(--gray)' }}>
            Register device-based passkeys for passwordless authentication. This flow requires an active Gate.haus session.
          </p>
        </div>
        <div>
          <strong>Manage passkeys</strong>
          <p style={{ color: 'var(--gray)' }}>
            You will be redirected to the Gate.haus passkey manager to create or update a passkey.
          </p>
          <Button type="button" onClick={openPasskeyManager}>
            Open passkey setup
          </Button>
        </div>
      </div>
      {status && <p style={{ color: 'var(--success)' }}>{status}</p>}
      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}
    </div>
  );
};

export default SecuritySettings;
